import {
  Children,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type ComponentType,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Check, ChevronDown, PenLine, Settings2, Trash2 } from 'lucide-react'
import {
  appBtnClassName,
  protoBtnProps,
  type AppBtnSize,
  type AppBtnTone,
  type AppBtnVariant,
} from '@/shared/lib/app-button'
import { DismissableLayerBranch } from '@radix-ui/react-dismissable-layer'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { SegmentedTabs } from '@/shared/components/ui/tabs'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import {
  PRODUCTION_CHIP_ACTIVE_CLASS,
  PRODUCTION_CHIP_INACTIVE_CLASS,
  PRODUCTION_EMPTY_DASHED_CLASS,
  PRODUCTION_SEGMENTED_LIST_CLASS,
  PRODUCTION_SEGMENTED_TRIGGER_CLASS,
  productionCardClass,
  productionListTableClass,
  productionStatClass,
} from '@/features/studio/lib/production-shell'

const PROTO_DROPDOWN_OUTSIDE_SELECTOR =
  '[data-proto-dropdown-root], .proto-dropdown-menu, [data-slot="select-content"], [data-slot="dropdown-menu-content"]'

/** Radix Dialog/Sheet：点击 portaled 的 ProtoDropdown 浮层时不视为外部点击 */
export function isProtoDropdownOutsideTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(PROTO_DROPDOWN_OUTSIDE_SELECTOR))
}

function getOutsideInteractionTarget(event: {
  target: EventTarget | null
  detail?: { originalEvent?: Event }
}): EventTarget | null {
  const original = event.detail?.originalEvent
  if (original && 'target' in original) {
    return original.target
  }
  return event.target
}

export function preventDismissOnProtoDropdown(event: {
  preventDefault: () => void
  target: EventTarget | null
  detail?: { originalEvent?: Event }
}) {
  if (isProtoDropdownOutsideTarget(getOutsideInteractionTarget(event))) {
    event.preventDefault()
  }
}

const protoDropdownDismissGuard = {
  onPointerDownOutside: preventDismissOnProtoDropdown,
  onInteractOutside: preventDismissOnProtoDropdown,
  onFocusOutside: preventDismissOnProtoDropdown,
}

/* ── 布局 ── */

export function ProtoBanner() {
  return (
    <div className="shrink-0 border-b border-amber-500/20 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-amber-500/10 px-4 py-2 text-center text-xs text-amber-200/90">
      <span className="font-medium text-amber-100">高保真原型</span>
      <span className="mx-2 text-amber-500/40">·</span>
      仿真 Mock 数据 · 无需登录 · 仅供 UI 评审
    </div>
  )
}

export const CARD = 'rounded-2xl bg-card text-card-foreground ring-1 ring-border shadow-sm'

export function ProtoCard({
  className,
  children,
  production,
}: {
  className?: string
  children: ReactNode
  production?: boolean
}) {
  return (
    <Card className={cn(production ? productionCardClass(className) : cn('gap-0 p-5 shadow-sm', className))}>
      {children}
    </Card>
  )
}

export function ProtoSectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-zinc-100">{children}</h2>
      {action}
    </div>
  )
}

/* ── 按钮 ── */

export type ProtoBtnVariant = AppBtnVariant
export type ProtoBtnTone = AppBtnTone
export type ProtoBtnSize = AppBtnSize

/** 页头 / 表格 / 卡片统一按钮 class（Link 等可复用） */
export function protoBtnClassName(options: {
  variant?: ProtoBtnVariant
  size?: ProtoBtnSize
  tone?: ProtoBtnTone
  className?: string
}) {
  return appBtnClassName(options)
}

export function ProtoBtn({
  variant = 'primary',
  size = 'md',
  tone = 'amber',
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ProtoBtnVariant
  size?: ProtoBtnSize
  tone?: ProtoBtnTone
}) {
  return (
    <Button type="button" {...protoBtnProps({ variant, size, tone, className })} {...props}>
      {children}
    </Button>
  )
}

/** 卡片 / 表格行内图标按钮 */
export function ProtoIconBtn({
  tone = 'default',
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'default' | 'danger'
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className={cn(
        tone === 'danger' && 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  )
}

/** 剧本列表行内操作 — 统一 text-xs（与编写按钮一致） */
const scriptRowActionTextClass = 'text-xs font-medium'

/** 剧本列表 · 编辑资料（ghost + Settings2，次要操作） */
export function ScriptMetaEditBtn({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <ProtoBtn size="sm" variant="ghost" className={cn(scriptRowActionTextClass, className)} {...props}>
      <Settings2 className="h-3.5 w-3.5" />
      {children}
    </ProtoBtn>
  )
}

/** 剧本列表 · 进入正文编写（soft amber，主操作） */
export function ScriptWriteLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={protoBtnClassName({
        size: 'sm',
        variant: 'soft',
        tone: 'amber',
        className: scriptRowActionTextClass,
      })}
    >
      <PenLine className="h-3.5 w-3.5" />
      {children}
    </Link>
  )
}

/** 剧本列表 · 删除（danger；`iconOnly` 时仅图标） */
export function ScriptDeleteBtn({
  iconOnly,
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { iconOnly?: boolean }) {
  if (iconOnly) {
    return (
      <ProtoIconBtn tone="danger" className={className} {...props}>
        <Trash2 className="h-3.5 w-3.5" />
      </ProtoIconBtn>
    )
  }
  return (
    <ProtoBtn size="sm" variant="danger" className={cn(scriptRowActionTextClass, className)} {...props}>
      <Trash2 className="h-3.5 w-3.5" />
      {children}
    </ProtoBtn>
  )
}

/** 图标切换（如列表/网格视图） */
export function ProtoIconSegmented<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: Array<{ value: T; icon: ComponentType<{ className?: string }>; label: string }>
  value: T
  onChange: (v: T) => void
  className?: string
}) {
  return (
    <div className={cn('inline-flex rounded-lg border border-border bg-muted/30 p-0.5', className)}>
      {options.map((opt) => {
        const Icon = opt.icon
        return (
          <Button
            key={opt.value}
            type="button"
            aria-label={opt.label}
            title={opt.label}
            variant={value === opt.value ? 'secondary' : 'ghost'}
            size="icon-sm"
            onClick={() => onChange(opt.value)}
          >
            <Icon className="h-4 w-4" />
          </Button>
        )
      })}
    </div>
  )
}

/* ── 表单 ── */

export function ProtoLabel({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">
      {children}
      {required ? <span className="ml-0.5 text-destructive">*</span> : null}
    </Label>
  )
}

export function ProtoInput({
  className,
  inputSize = 'default',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { inputSize?: 'default' | 'compact' }) {
  return (
    <Input
      className={cn(inputSize === 'compact' && 'h-7 px-2 text-xs', className)}
      {...props}
    />
  )
}

/** 可自由键入的整数输入（无原生步进箭头；聚焦时不同步外部 value，避免无法清空重输） */
export function ProtoNumberInput({
  value,
  onValueChange,
  min = 0,
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
  value: number
  onValueChange: (value: number) => void
  min?: number
}) {
  const [draft, setDraft] = useState(() => String(value))
  const focusedRef = useRef(false)

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(String(value))
    }
  }, [value])

  function commit(raw: string) {
    const parsed = parseInt(raw, 10)
    const next = Number.isFinite(parsed) ? Math.max(min, parsed) : min
    setDraft(String(next))
    onValueChange(next)
  }

  return (
    <ProtoInput
      type="text"
      inputMode="numeric"
      className={className}
      value={draft}
      onFocus={(e) => {
        focusedRef.current = true
        props.onFocus?.(e)
      }}
      onBlur={(e) => {
        focusedRef.current = false
        commit(draft)
        props.onBlur?.(e)
      }}
      onChange={(e) => {
        const raw = e.target.value.replace(/\D/g, '')
        setDraft(raw)
        if (raw !== '') {
          const parsed = parseInt(raw, 10)
          if (Number.isFinite(parsed)) {
            onValueChange(Math.max(min, parsed))
          }
        }
      }}
      {...props}
    />
  )
}

/* ── 下拉（对齐 PortalLangSwitcher）── */

export type ProtoDropdownOption = {
  value: string
  label: ReactNode
  /** 右侧固定展示（如积分），不参与 truncate */
  badge?: ReactNode
  disabled?: boolean
}

export function ProtoDropdown({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  className,
  compact,
  align = 'left',
  placement = 'bottom',
  portal = false,
  triggerClassName,
  'aria-label': ariaLabel,
}: {
  value: string
  onValueChange: (value: string) => void
  options: ProtoDropdownOption[]
  placeholder?: string
  disabled?: boolean
  className?: string
  compact?: boolean
  align?: 'left' | 'right'
  placement?: 'bottom' | 'top'
  /** 浮层挂到 body，避免画布节点 overflow 裁切 */
  portal?: boolean
  triggerClassName?: string
  'aria-label'?: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number } | null>(null)
  const selected = options.find((o) => o.value === value)
  const label = selected?.label ?? placeholder ?? '—'
  const triggerTitle =
    typeof label === 'string' || typeof label === 'number' ? String(label) : undefined

  const updateMenuPosition = () => {
    if (!rootRef.current) return
    const rect = rootRef.current.getBoundingClientRect()
    const menuHeight = menuRef.current?.offsetHeight ?? 280
    const gap = 6
    const top =
      placement === 'top'
        ? Math.max(8, rect.top - menuHeight - gap)
        : Math.min(window.innerHeight - 8, rect.bottom + gap)
    const width = Math.max(rect.width, 160)
    let left = align === 'right' ? rect.right - width : rect.left
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
    setMenuStyle({ top, left, width })
  }

  useLayoutEffect(() => {
    if (!open || !portal) return
    updateMenuPosition()
  }, [open, portal, placement, align, options.length])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onRelayout = () => {
      if (portal) updateMenuPosition()
    }
    /** 画布 React Flow 在 document 上监听 wheel 缩放；portal 菜单须拦截冒泡/捕获，否则列表无法滚动 */
    const onWheelCapture = (e: WheelEvent) => {
      const menu = menuRef.current
      if (!menu?.contains(e.target as Node)) return
      e.stopPropagation()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('wheel', onWheelCapture, { capture: true })
    window.addEventListener('resize', onRelayout)
    window.addEventListener('scroll', onRelayout, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('wheel', onWheelCapture, { capture: true })
      window.removeEventListener('resize', onRelayout)
      window.removeEventListener('scroll', onRelayout, true)
    }
  }, [open, portal])

  const menuList = (
    <ul
      className="proto-dropdown-menu studio-scrollbar-hidden max-h-72 overflow-y-auto overscroll-contain px-1 pb-1 nowheel nodrag nopan"
      onWheel={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {options.map((opt) => {
        const isSelected = opt.value === value
        return (
          <li key={opt.value} role="presentation">
            <Button
              type="button"
              role="option"
              aria-selected={isSelected}
              disabled={opt.disabled}
              variant={isSelected ? 'secondary' : 'ghost'}
              className="h-auto min-h-[44px] w-full justify-start px-2.5 py-2 text-sm font-normal"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                if (opt.disabled) return
                onValueChange(opt.value)
                setOpen(false)
              }}
            >
              <span className="min-w-0 flex-1 truncate text-left">{opt.label}</span>
              {opt.badge != null && opt.badge !== '' ? (
                <span className="shrink-0 text-xs text-muted-foreground">{opt.badge}</span>
              ) : null}
              {isSelected ? <Check size={14} className="shrink-0 text-primary" aria-hidden /> : null}
            </Button>
          </li>
        )
      })}
    </ul>
  )

  const menuPanel = open ? (
    <DismissableLayerBranch
      role="listbox"
      className={clsx(
        'proto-dropdown-menu pointer-events-auto overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-lg ring-1 ring-border/50 nowheel nodrag nopan',
        portal ? 'fixed z-[9999]' : clsx('absolute z-[110] w-full', placement === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5', align === 'right' ? 'right-0' : 'left-0'),
      )}
      onWheel={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      style={portal && menuStyle ? { top: menuStyle.top, left: menuStyle.left, width: menuStyle.width } : undefined}
    >
      <div ref={menuRef}>{menuList}</div>
    </DismissableLayerBranch>
  ) : null

  return (
    <div
      ref={rootRef}
      data-proto-dropdown-root
      className={clsx('relative', className)}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Button
        type="button"
        variant="outline"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={triggerTitle}
        className={clsx(
          'cn-control-trigger h-auto w-full justify-between gap-2 text-left font-normal',
          compact ? 'h-7 min-h-7 px-2 text-xs' : 'h-8 min-h-8 px-2.5 text-sm',
          open && 'border-primary/45 bg-accent/40',
          triggerClassName,
        )}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {selected?.badge != null && selected.badge !== '' ? (
          <span className={clsx('shrink-0 text-muted-foreground', compact ? 'text-[10px]' : 'text-xs')}>
            {selected.badge}
          </span>
        ) : null}
        <ChevronDown
          size={14}
          className={clsx('shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')}
          aria-hidden
        />
      </Button>

      {portal ? (menuPanel ? createPortal(menuPanel, document.body) : null) : menuPanel}
    </div>
  )
}

function parseSelectOptions(children: ReactNode): ProtoDropdownOption[] {
  const opts: ProtoDropdownOption[] = []
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === 'option') {
      const props = child.props as { value?: string | number; disabled?: boolean; children?: ReactNode }
      opts.push({
        value: String(props.value ?? ''),
        label: props.children,
        disabled: props.disabled,
      })
    }
  })
  return opts
}

export function ProtoSelect({
  className,
  children,
  value,
  onChange,
  disabled,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  const options = useMemo(() => parseSelectOptions(children), [children])
  const compact = className?.includes('!text-xs') || className?.includes('text-xs')

  return (
    <Select
      value={String(value ?? '')}
      onValueChange={(v) => {
        onChange?.({ target: { value: v } } as ChangeEvent<HTMLSelectElement>)
      }}
      disabled={disabled}
    >
      <SelectTrigger
        className={cn(
          'cn-control-trigger h-auto w-full justify-between gap-2 text-left font-normal',
          compact ? 'h-7 min-h-7 px-2 text-xs' : 'h-8 min-h-8 px-2.5 text-sm',
          className,
        )}
        aria-label={props['aria-label']}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper" sideOffset={4} className="z-[10000] max-h-72">
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/* ── 分页 ── */

export function ProtoPagination({
  page,
  pageSize,
  total,
  onPageChange,
  className,
  prevLabel = '上一页',
  nextLabel = '下一页',
}: {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  className?: string
  prevLabel?: string
  nextLabel?: string
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const canPrev = page > 0
  const canNext = page < pageCount - 1

  if (total <= pageSize) return null

  return (
    <div className={clsx('mt-6 flex items-center justify-center gap-2 text-sm', className)}>
      <ProtoBtn size="sm" variant="secondary" disabled={!canPrev} onClick={() => onPageChange(page - 1)}>
        {prevLabel}
      </ProtoBtn>
      <span className="text-xs text-zinc-500">
        {page + 1} / {pageCount}
      </span>
      <ProtoBtn size="sm" variant="secondary" disabled={!canNext} onClick={() => onPageChange(page + 1)}>
        {nextLabel}
      </ProtoBtn>
    </div>
  )
}

/* ── 列表表格壳 ── */

export function ProtoListTable({
  children,
  className,
  production,
}: {
  children: ReactNode
  className?: string
  production?: boolean
}) {
  return (
    <Card
      className={cn(
        production
          ? productionListTableClass(className)
          : cn('gap-0 overflow-hidden p-0 shadow-sm ring-1 ring-border', className),
      )}
    >
      <div className="studio-scrollbar-hidden overflow-x-auto">{children}</div>
    </Card>
  )
}

export { listTableHeadRowClass, listTableRowClass } from '@/shared/components/DropdownSelect'

export function ProtoTextarea({ className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <Textarea className={cn('min-h-20', className)} {...props} />
}

/* ── Badge / Chip ── */

const BADGE_TONE_CLASS: Record<string, string> = {
  default: 'border-border bg-secondary text-secondary-foreground',
  indigo: 'border-indigo-500/25 bg-indigo-500/10 text-indigo-200',
  emerald: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200',
  amber: 'border-amber-500/25 bg-amber-500/10 text-amber-200',
  rose: 'border-rose-500/25 bg-rose-500/10 text-rose-200',
  sky: 'border-sky-500/25 bg-sky-500/10 text-sky-200',
  violet: 'border-violet-500/25 bg-violet-500/10 text-violet-200',
}

export function ProtoBadge({
  children,
  tone = 'default',
  className,
}: {
  children: ReactNode
  tone?: keyof typeof BADGE_TONE_CLASS
  className?: string
}) {
  return (
    <Badge variant="outline" className={cn('rounded-full text-[10px]', BADGE_TONE_CLASS[tone] ?? BADGE_TONE_CLASS.default, className)}>
      {children}
    </Badge>
  )
}

export function ProductionEmptyPanel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        PRODUCTION_EMPTY_DASHED_CLASS,
        'flex min-h-[280px] flex-col items-center justify-center p-8 text-center text-sm text-zinc-500',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function ProtoChip({
  children,
  active,
  onClick,
  production,
}: {
  children: ReactNode
  active?: boolean
  onClick?: () => void
  production?: boolean
}) {
  return (
    <Button
      type="button"
      variant={production ? 'outline' : active ? 'accent' : 'outline'}
      size="xs"
      className={cn(
        'rounded-full px-2.5',
        production && (active ? PRODUCTION_CHIP_ACTIVE_CLASS : PRODUCTION_CHIP_INACTIVE_CLASS),
      )}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

/* ── 头像 ── */

export function ProtoAvatar({ label, size = 'md' }: { label: string; size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'sm' ? 'h-7 w-7 text-[10px]' : size === 'lg' ? 'h-11 w-11 text-sm' : 'h-9 w-9 text-xs'
  return (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500/30 to-violet-600/20 font-semibold text-indigo-100 ring-2 ring-[#0b0b13]',
        sz,
      )}
    >
      {label.slice(0, 2)}
    </div>
  )
}

/* ── 进度条 ── */

export function ProtoProgress({ value, max, label }: { value: number; max: number; label?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div>
      {label ? (
        <div className="mb-1.5 flex justify-between text-[11px] text-zinc-500">
          <span>{label}</span>
          <span>
            {value}/{max}
          </span>
        </div>
      ) : null}
      <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
        <div
          className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

/* ── Segmented / Tabs ── */

export function ProtoSegmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <SegmentedTabs
      options={options}
      value={value}
      onChange={onChange}
      size="xs"
    />
  )
}

export function ProtoTabs<T extends string>({
  tabs,
  active,
  onChange,
  production,
}: {
  tabs: { id: T; label: string }[]
  active: T
  onChange: (id: T) => void
  production?: boolean
}) {
  return (
    <SegmentedTabs
      options={tabs.map((tab) => ({ value: tab.id, label: tab.label }))}
      value={active}
      onChange={onChange}
      className="w-full"
      listClassName={production ? PRODUCTION_SEGMENTED_LIST_CLASS : undefined}
      triggerClassName={production ? PRODUCTION_SEGMENTED_TRIGGER_CLASS : undefined}
    />
  )
}

/* ── 缩略图占位 ── */

export function ProtoThumb({
  seed,
  className,
  aspect = 'video',
  flush = false,
}: {
  seed: string
  className?: string
  aspect?: 'video' | 'square' | 'portrait'
  /** 卡片封面内铺满：去掉圆角与描边，与正文区直角衔接 */
  flush?: boolean
}) {
  const hues = [240, 260, 200, 320, 180, 30]
  const h = hues[seed.charCodeAt(0) % hues.length]
  const ratio = aspect === 'square' ? 'aspect-square' : aspect === 'portrait' ? 'aspect-[9/16]' : 'aspect-video'
  return (
    <div
      className={clsx(
        'overflow-hidden',
        flush ? 'rounded-none border-0' : 'rounded-xl border border-white/[0.06]',
        ratio,
        className,
      )}
      style={{
        background: `linear-gradient(135deg, hsl(${h} 40% 18%) 0%, hsl(${(h + 40) % 360} 35% 12%) 100%)`,
      }}
    />
  )
}

/* ── Modal ── */

export function ProtoModal({
  open,
  onClose,
  title,
  wide,
  size,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  wide?: boolean
  size?: 'md' | 'lg' | 'xl'
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton
        {...protoDropdownDismissGuard}
        className={cn(
          'gap-0 overflow-hidden p-0 sm:max-w-lg',
          size === 'xl' ? 'sm:max-w-5xl' : size === 'lg' || wide ? 'sm:max-w-2xl' : null,
        )}
      >
        <DialogHeader className="border-b px-5 py-4 text-left">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="studio-scrollbar-hidden max-h-[calc(90vh-8rem)] overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <DialogFooter className="-mx-0 -mb-0 w-full gap-0 border-t bg-transparent px-5 py-4 sm:justify-end">
            {footer}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

/* ── Drawer ── */

export function ProtoDrawer({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}) {
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="flex w-full max-w-md flex-col gap-0 p-0" {...protoDropdownDismissGuard}>
        <SheetHeader className="border-b px-5 py-4 text-left">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="studio-scrollbar-hidden flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </SheetContent>
    </Sheet>
  )
}

/* ── Stat ── */

export function ProtoStat({
  label,
  value,
  production,
}: {
  label: string
  value: string
  production?: boolean
}) {
  return (
    <div className={production ? productionStatClass() : clsx(CARD, 'p-4')}>
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-zinc-100">{value}</p>
    </div>
  )
}

/* ── Kanban 列 ── */

export function ProtoKanbanColumn({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: ReactNode
}) {
  return (
    <div className="min-w-[220px] flex-1 rounded-2xl border border-white/[0.04] bg-[#0e0e16]/80 p-3">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-medium text-zinc-400">{title}</span>
        <ProtoBadge tone="default">{count}</ProtoBadge>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}
